/*----------------------------------------------------------------------------
|
|  simple JSON Viewer/Editor and JSON path selector
|
|
|  based on:
|
|  React JSONTree
|  http://eskimospy.com/stuff/react/json/
|  Copyright 2014, David Vedder
|  MIT Licence
| 
|  but enhanced with more capabilities, mainly:
|  a) ability to provide an JSONPath when clicking on a plus sign in the UI
|  b) ability to edit value directly in the UI which changes the given
|     javascript object (provide an overall change event callback)
| 
|  written by
|  Jochen Loewer, Jan 2015
|
\----------------------------------------------------------------------------*/
import React from 'react';
import ReactDOM from 'react-dom';

class TakeNode extends React.Component {

  constructor(props) {
    super(props);
    // autobinding 
    // https://reactjs.org/docs/react-without-es6.html#autobinding
    this.handleTake = this.handleTake.bind(this);      
  }

  handleTake(path) {
    if (this.props.all.takeFunc) {
      this.props.all.takeFunc(path);
    }
  }
  render() {
    var path = '?';
    if (this.props.all.path) {
      path = this.props.all.path;
    }
    return (
      <label><i className="glyphicon glyphicon-plus-sign" onClick={this.handleTake.bind(null, path)}></i>&nbsp;{this.props.all.keyName}</label>
    )
  }
}

/**
 * Creates a React JSON Viewer component for a key and it's associated data
 *
 * @param key String The JSON key (property name) for the node
 * @param value Mixed The associated data for the JSON key
 * @return Component The React Component for that node
 */
var grabNode = function (takeFunc, onChange, path, key, value, refObj, refIndex) {
    var nodeType = objType(value);
    var theNode;
    var aKey = key + Date.now();
    if (nodeType === 'Object') {
        theNode = <JSONObjectNode data={value} keyName={key} key={aKey} path={path} takeFunc={takeFunc} onChange={onChange} />
    } else if (nodeType === 'Array') {
        theNode = <JSONArrayNode data={value}  keyName={key} key={aKey} path={path} takeFunc={takeFunc} onChange={onChange} />
    } else if (nodeType === 'String') {
        theNode = <JSONStringNode keyName={key} value={value} refObject={refObj} refIndex={refIndex} key={aKey} path={path} takeFunc={takeFunc} onChange={onChange} />
    } else if (nodeType === 'Number') {
        theNode = <JSONNumberNode keyName={key} value={value} refObject={refObj} refIndex={refIndex} key={aKey} path={path} takeFunc={takeFunc} onChange={onChange} />
    } else if (nodeType === 'Boolean') {
        theNode = <JSONBooleanNode keyName={key} value={value} refObject={refObj} refIndex={refIndex} key={aKey} path={path} takeFunc={takeFunc} onChange={onChange} />
    } else if (nodeType === 'Null') {
        theNode = <JSONNullNode keyName={key} value={value} refObject={refObj} refIndex={refIndex} key={aKey} path={path} takeFunc={takeFunc} onChange={onChange} />
    } else {
        console.error("How did this happen?", nodeType);
    }
    return theNode;
};

/**
 * Returns the type of an object as a string.
 *
 * @param obj Object The object you want to inspect
 * @return String The object's type
 */
var objType  = function (obj) {
    var className = Object.prototype.toString.call(obj).slice(8, -1);
    return className;
}

/**
 * Mixin for stopping events from propagating and collapsing our tree all
 * willy nilly. 
 */
var SquashClickEventMixin = {
    handleClick: function (e) {
        e.stopPropagation();
    }
};

/**
 * Mixin for setting intial props and state and handling clicks on
 * nodes that can be expanded.
 */
var ExpandedStateHandlerMixin = {
    getDefaultProps: function () {
        return {data:[], initialExpanded: false};
    },
    getInitialState: function () {
        return {
            expanded: this.props.initialExpanded,
            createdChildNodes: false
        };
    },
    handleClick: function (e) {
        e.stopPropagation();
        this.setState({expanded: !this.state.expanded});
    },
    componentWillReceiveProps: function () {
        // resets our caches and flags we need to build child nodes again
        this.renderedChildren = [];
        this.itemString = false
        this.needsChildNodes= true;
    }
};


/**
 * Array node class. If you have an array, this is what you should use to 
 * display it.
 */
class JSONArrayNode extends React.Component {

    // defaultProps (moved below class definition)

    // getInitialState
    // https://reactjs.org/docs/react-without-es6.html#setting-the-initial-state
    constructor(props) {
      super(props);

      this.needsChildNodes = true;
      this.renderedChildren = [];
      this.itemString = false;

      this.state = {
        expanded: this.props.initialExpanded,
        createdChildNodes: false
      };
      // autobinding 
      // https://reactjs.org/docs/react-without-es6.html#autobinding
      this.handleClick = this.handleClick.bind(this);      
    }

    handleClick(e) {
        e.stopPropagation();
        this.setState({expanded: !this.state.expanded});
    }
    componentWillReceiveProps() {
        // resets our caches and flags we need to build child nodes again
        this.renderedChildren = [];
        this.itemString = false
        this.needsChildNodes= true;
    }

    /**
     * Returns the child nodes for each element in the array. If we have
     * generated them previously, we return from cache, otherwise we create 
     * them.
     */
    getChildNodes() {  
        var childNodes = [];
        if (this.state.expanded && this.needsChildNodes) {
            for (var i = 0; i < this.props.data.length; i += 1) {
                childNodes.push( grabNode(this.props.takeFunc, this.props.onChange, this.props.path+'['+i+']', '[ '+i+' ] ', 
                                          this.props.data[i], this.props.data, i
                                 )
                               );
            }
            this.needsChildNodes = false;
            this.renderedChildren = childNodes;
        }
        return this.renderedChildren;
    }

    /**
     * Returns the "n Items" string for this node, generating and
     * caching it if it hasn't been created yet.
     */


    getItemString() {
        if (!this.itemString) {
            var lenWord = (this.props.data.length === 1) ? ' item' : ' array items';
            this.itemString = this.props.data.length + lenWord;
        }
        return this.itemString;
    }
    render() {
        var childNodes = this.getChildNodes();
        var childListStyle = {
            display: (this.state.expanded) ? 'block' : 'none'
        };
        var cls = "array parentNode";
        cls += (this.state.expanded) ? " expanded" : '';
        return (
            <li className={cls} onClick={this.handleClick}>
                <TakeNode all={this.props}/>
                <span>{this.getItemString()}</span>
                <ol style={childListStyle}>
                    {childNodes}
                </ol>
            </li>
        );
    }
};

// defaultProps
// https://reactjs.org/docs/react-component.html#defaultprops
JSONArrayNode.defaultProps = {
  data:[], initialExpanded: false,
};

/**
 * Object node class. If you have an object, this is what you should use to 
 * display it.
 */
class JSONObjectNode extends React.Component {

    // defaultProps (moved below class definition)

    // getInitialState
    // https://reactjs.org/docs/react-without-es6.html#setting-the-initial-state
    constructor(props) {
      super(props);

      this.needsChildNodes = true;
      this.renderedChildren = [];
      this.itemString = false;

      this.state = {
        expanded: this.props.initialExpanded,
        createdChildNodes: false
      };
      // autobinding 
      // https://reactjs.org/docs/react-without-es6.html#autobinding
      this.handleClick = this.handleClick.bind(this);
    }

    handleClick(e) {
        e.stopPropagation();
        this.setState({expanded: !this.state.expanded});
    }
    componentWillReceiveProps() {
        // resets our caches and flags we need to build child nodes again
        this.renderedChildren = [];
        this.itemString = false
        this.needsChildNodes= true;
    }
    /**
     * Returns the child nodes for each element in the object. If we have
     * generated them previously, we return from cache, otherwise we create 
     * them.
     */
    getChildNodes() {
        if (this.state.expanded && this.needsChildNodes) {
            var obj = this.props.data;
            var childNodes = [];
            for (var k in obj) {
                if (obj.hasOwnProperty(k)) {
                    childNodes.push( grabNode(this.props.takeFunc, this.props.onChange, this.props.path + '.'+k, k, 
                                              obj[k], obj, k
                                     )
                                   );
                }
            }
            this.needsChildNodes = false;
            this.renderedChildren = childNodes;
        }
        return this.renderedChildren;
    }
    /**
     * Returns the "n Items" string for this node, generating and
     * caching it if it hasn't been created yet.
     */
    getItemString() {
        if (!this.itemString) {
            var obj = this.props.data;
            var len = 0;
            var lenWord = ' objects';
            for (var k in obj) {
                if (obj.hasOwnProperty(k)) {
                    len += 1;
                }
            }
            if (len === 1) {
                lenWord = ' object';
            }
            this.itemString = len + lenWord
        }
        return this.itemString;
    }

    render() {
        var childListStyle = {
            display: (this.state.expanded) ? 'block' : 'none'
        };
        var cls = "object parentNode";
        cls += (this.state.expanded) ? " expanded" : '';
        return (
            <li className={cls} onClick={this.handleClick}>
                <TakeNode all={this.props}/>
                <span>{this.getItemString()}</span>
                <ul style={childListStyle}>
                    { this.getChildNodes() }
                </ul>
            </li>
        );
    }
};

// defaultProps
// https://reactjs.org/docs/react-component.html#defaultprops
JSONObjectNode.defaultProps = {
  data:[], initialExpanded: false,
};

function makeEditable(self, node, onChange)
{      
      var len = self.props.value.length+4;

      $(node.firstChild).replaceWith('<input type=text value="" style="padding: 1px 1px 0px 1px; border: 0px solid #ddd; width: '+len+'ex;}">'); 
      $(node.firstChild).val(self.props.value);

      var self2 = self;

        if (self2.props.path.search("ctx")) {
            var treeObj = "ctx";
        } else {
            var treeObj = "order"
        }

      $(node.firstChild).blur(function() {
        var v = $(node.firstChild).val();
        if (v != undefined) {
          window.legacystate.data[treeObj][self2.props.refIndex] = v;
          //self2.props.value = v;
          //self2.props.refObject[self2.props.refIndex] = v;
          $(node.firstChild).remove();
          var a = $(node).text(v);
          if (onChange) {
            onChange();
          }
       }
      });
      $(node.firstChild).keypress(function( event ) {
        if ( event.which == 13 ) {
          var v = $(node.firstChild).val();
          if (v != undefined) {
            window.legacystate.data[treeObj][self2.props.refIndex] = v;
            //self2.props.value = v;
            //self2.props.refObject[self2.props.refIndex] = v;
            $(node.firstChild).remove();
            var a = $(node).text(v);
            event.preventDefault();
            if (onChange) {
              onChange();
            }
            return;
          }
        }
      });
      $(node.firstChild).focus();
}

/**
 * String node component
 */
class JSONStringNode extends React.Component {

    constructor(props) {
      super(props);
      // autobinding 
      // https://reactjs.org/docs/react-without-es6.html#autobinding
      this.handleClick = this.handleClick.bind(this);
      this.onStartEdit = this.onStartEdit.bind(this);  
    }  

    handleClick(e) {
        e.stopPropagation();
    }

    onStartEdit(e) {
      makeEditable(this, e.target, this.props.onChange );
    }
    render() {
        return (
            <li className="string itemNode" onClick={this.handleClick}>
                <TakeNode all={this.props}/>
                <span onClick={this.onStartEdit}>{this.props.value}</span>
            </li>
        );
    }
};

/**
 * Number node component
 */

class JSONNumberNode extends React.Component {

    constructor(props) {
      super(props);
      // autobinding 
      // https://reactjs.org/docs/react-without-es6.html#autobinding
      this.handleClick = this.handleClick.bind(this);
      this.onStartEdit = this.onStartEdit.bind(this); 
    }

    handleClick(e) {
        e.stopPropagation();
    }
    onStartEdit(e) {
      makeEditable(this, e.target, this.props.onChange );
    }
    render() {
        return (
            <li className="number itemNode" onClick={this.handleClick}>
                <TakeNode all={this.props}/>
                <span onClick={this.onStartEdit}>{this.props.value}</span>
            </li>
        );
    }
};


/**
 * Null node component
 */
class JSONNullNode extends React.Component {

    constructor(props) {
      super(props);
      // autobinding 
      // https://reactjs.org/docs/react-without-es6.html#autobinding
      this.handleClick = this.handleClick.bind(this);
      this.onStartEdit = this.onStartEdit.bind(this); 
    }

    handleClick(e) {
        e.stopPropagation();
    }
    onStartEdit(e) {
      makeEditable(this, e.target, this.props.onChange );
    }
    render() {
        return (
            <li className="null itemNode" onClick={this.handleClick}>
                <TakeNode all={this.props}/>
                <span onClick={this.onStartEdit}>null</span>
            </li>
        );
    }
};

/**
 * Boolean node component
 */
class JSONBooleanNode extends React.Component {

    constructor(props) {
      super(props);
      // autobinding 
      // https://reactjs.org/docs/react-without-es6.html#autobinding
      this.handleClick = this.handleClick.bind(this);
      this.onStartEdit = this.onStartEdit.bind(this); 
    }

    handleClick(e) {
        e.stopPropagation();
    }
    onStartEdit(e) {
      makeEditable(this, e.target, this.props.onChange );
    }
    render() {
        var truthString = (this.props.value) ? 'true' : 'false';
        return (
            <li className={"boolean itemNode " + truthString} onClick={this.handleClick}>
                <TakeNode all={this.props}/>
                <span onClick={this.onStartEdit}>{truthString}</span>
            </li>
        );
    }
};

/**
 * JSONTree component. This is the 'viewer' base. Pass it a `data` prop and it 
 * will render that data, or pass it a `source` URL prop and it will make 
 * an XMLHttpRequest for said URL and render that when it loads the data.
 * 
 * You can load new data into it by either changing the `data` prop or calling
 * `loadDataFromURL()` on an instance.
 *
 * The first node it draws will be expanded by default. 
 */

export default class JSONTree extends React.Component {

    /**
     * Will try and load data from the given URL and display it
     */
    loadDataFromURL(url) {
        var self = this;
        var request = new XMLHttpRequest();
        request.open('GET', url, true);
        request.onload = function() {
            if (request.status >= 200 && request.status < 400){
                jsonData = JSON.parse(request.responseText);
                self.setProps({
                    data: jsonData
                });
            } else {
                console.error("OH NO! Your request was bad.");
            }
        };
        request.onerror = function() {
            console.error("Connection error");
        };
        request.send();
    }
    componentDidMount() {
        if (this.props.source) {
            this.loadDataFromURL(this.props.source);
        }
    }
    render() {
        var nodeType = objType(this.props.data);
        var rootNode;
        if (nodeType === 'Object') {
            rootNode = <JSONObjectNode data={this.props.data} keyName="root" path="root" initialExpanded={true} takeFunc={this.props.takeFunc} onChange={this.props.onChange}/>
        } else if (nodeType === 'Array') {
            rootNode = <JSONArrayNode data={this.props.data} initialExpanded={true} keyName="root" path="root" takeFunc={this.props.takeFunc} onChange={this.props.onChange}/>
        } else {
            console.error("How did you manage that?");
        }
        return (
            <ul className="jsonTree">
                { rootNode }
            </ul>
        );
    }
};

// defaultProps
// https://reactjs.org/docs/react-component.html#defaultprops
JSONTree.defaultProps = {
  source: false
};
